import { InboxOutlined, LockOutlined, ThunderboltOutlined, AimOutlined } from '@ant-design/icons';
import { message, Modal, Progress, Upload } from 'antd';
import type { ModalProps, UploadFile, UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { useRef, useState } from 'react';

import { uploadResume } from '@/services/user';
import type { ResumeDTO } from '@/types/resume';
import oss from '@/utils/oss';
import { useNavigate } from 'react-router-dom';

const { Dragger } = Upload;

interface ResumeUploadProps extends ModalProps {
  onUploadSuccess?: (resume: ResumeDTO) => void;
}

const DEFAULT_MAX_SIZE = 5; // 默认 5MB
const DEFAULT_ACCEPT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const DEFAULT_OBJECT_NAME_PREFIX = 'resume/';

const ResumeUpload = ({ onUploadSuccess, ...props }: ResumeUploadProps) => {
  const navigate = useNavigate();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const abortUploadRef = useRef<(() => void) | null>(null);

  // 处理文件上传前的验证
  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    // 检查文件类型
    if (!DEFAULT_ACCEPT.includes(file.type)) {
      message.error('只能上传 pdf doc docx 格式的文件！');
      return Upload.LIST_IGNORE;
    }

    // 检查文件大小
    const maxSizeBytes = DEFAULT_MAX_SIZE * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      message.error(`简历大小不能超过 ${DEFAULT_MAX_SIZE}MB！`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  // 处理文件上传
  const handleUpload: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
    const uploadFile = file as File;
    const rcFile = file as RcFile;
    setUploading(true);
    setUploadProgress(0);

    if (!window.config?.userNumber) {
      navigate('/login');
      return;
    }

    const objectName = `${DEFAULT_OBJECT_NAME_PREFIX}${window.config?.userNumber}.${uploadFile.name
      .split('.')
      .pop()}`;
    const uid = rcFile.uid || `${Date.now()}_${Math.random()}`;

    // 更新文件状态为上传中
    const uploadingFile: UploadFile = {
      uid,
      name: uploadFile.name,
      status: 'uploading',
      url: (rcFile as unknown as { url?: string }).url || URL.createObjectURL(uploadFile),
    };
    const newFileList = [uploadingFile];
    setFileList(newFileList);

    try {
      await oss.initialize();
      const result = await oss.uploadFile(uploadFile, objectName, (progress, abort) => {
        setUploadProgress(progress);
        abortUploadRef.current = abort;
      });
      // eslint-disable-next-line no-console
      console.log('oss result', result);
      const resume = await uploadResume(result.url);

      // 更新文件状态为完成
      const doneFile: UploadFile = {
        uid,
        name: uploadFile.name,
        status: 'done',
        url: result.url,
      };
      const finalFileList = [doneFile];
      setFileList(finalFileList);
      onSuccess?.(result, rcFile);

      message.success('简历上传成功');
      // 调用上传成功回调
      onUploadSuccess?.(resume);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '简历上传失败';
      message.error(errorMessage);
      // 上传失败时移除文件
      setFileList([]);
      onError?.(error as Error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      abortUploadRef.current = null;
    }
  };

  // 处理文件移除
  const handleRemove: UploadProps['onRemove'] = async () => {
    try {
      // 如果正在上传，先中止上传
      if (uploading && abortUploadRef.current) {
        abortUploadRef.current();
      }
    } finally {
      abortUploadRef.current = null;
      setUploading(false);
      setUploadProgress(0);
      const emptyFileList: UploadFile[] = [];
      setFileList(emptyFileList);
    }
    return true;
  };

  // 处理文件列表变化（用于拖拽等场景）
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    // 如果已经有文件，限制只能有一个
    if (newFileList.length > 1) {
      newFileList = [newFileList[newFileList.length - 1]];
    }
    setFileList(newFileList);
  };

  return (
    <Modal {...props} footer={null} wrapClassName="py-6 shadow-2xl relative">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-3">上传你的简历</h1>
        <p className="text-sm sm:text-base text-slate-500 leading-relaxed">
          支持 PDF、Word 格式，小灵将智能解析你的简历内容
        </p>
      </div>

      <Dragger
        fileList={fileList}
        beforeUpload={beforeUpload}
        customRequest={handleUpload}
        onRemove={handleRemove}
        onChange={handleChange}
        maxCount={1}
        accept={DEFAULT_ACCEPT.join(',')}
        listType="text"
      >
        <p className="ant-upload-drag-icon mb-4 text-indigo-500">
          <InboxOutlined className="text-5xl" />
        </p>
        <p className="ant-upload-text text-base font-semibold text-slate-800 mb-1">
          点击或将文件拖拽到此处上传
        </p>
        <p className="ant-upload-hint text-xs sm:text-sm text-slate-500">
          单个文件上传，大小与格式限制以后端配置为准。
        </p>
      </Dragger>
      {uploading && (
        <div className="mt-2">
          <Progress percent={Math.round(uploadProgress * 100)} />
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-slate-500 text-sm">
        <div className="flex items-center gap-2">
          <LockOutlined className="text-lg text-slate-600" />
          <span className="font-medium">安全加密</span>
        </div>
        <div className="flex items-center gap-2">
          <ThunderboltOutlined className="text-lg text-slate-600" />
          <span className="font-medium">快速解析</span>
        </div>
        <div className="flex items-center gap-2">
          <AimOutlined className="text-lg text-slate-600" />
          <span className="font-medium">精准匹配</span>
        </div>
      </div>
    </Modal>
  );
};

export default ResumeUpload;
